import { useState, useEffect, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { useLang } from "@/context/LanguageContext";
import translations from "@/data/translations";
import { AlertTriangle, MessageCircle, X } from "lucide-react";

const WHATSAPP_URL = "https://wa.me/34672808030?text=Hola%20OKontainers!%20Tengo%20una%20operaci%C3%B3n%20en%20curso%20que%20necesita%20ayuda%20urgente.";

const UrgencyBanner = () => {
  const { lang } = useLang();
  const t = translations[lang].urgency;
  const [show, setShow] = useState(false);

  const handleScroll = useCallback(() => {
    const dismissed = sessionStorage.getItem("okontainers_urgency_dismissed");
    if (dismissed) return;
    const pct = window.scrollY / (document.body.scrollHeight - window.innerHeight);
    if (pct > 0.35) setShow(true);
  }, []);

  useEffect(() => {
    window.addEventListener("scroll", handleScroll, { passive: true });
    return () => window.removeEventListener("scroll", handleScroll);
  }, [handleScroll]);

  const handleDismiss = () => {
    setShow(false);
    sessionStorage.setItem("okontainers_urgency_dismissed", "true");
  };

  if (!show) return null;

  return (
    <motion.div
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.4, type: "spring", damping: 25 }}
      data-testid="urgency-banner"
      className="fixed top-28 md:top-[116px] left-0 right-0 z-40 bg-gradient-to-r from-amber-500 to-orange-500 shadow-lg"
    >
      <div className="max-w-7xl mx-auto px-5 md:px-10 py-3 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <div className="shrink-0 w-8 h-8 rounded-full bg-white/20 flex items-center justify-center">
            <AlertTriangle size={16} className="text-white" />
          </div>
          <p className="text-white text-sm font-semibold font-body truncate md:whitespace-normal">
            {t.text}
          </p>
        </div>
        <div className="flex items-center gap-2 shrink-0">
          <a
            data-testid="urgency-cta"
            href={WHATSAPP_URL}
            target="_blank"
            rel="noopener noreferrer"
            className="inline-flex items-center gap-1.5 bg-white text-orange-600 font-bold text-xs md:text-sm py-2 px-4 md:px-5 rounded-full hover:bg-orange-50 transition-all shadow-sm"
          >
            <MessageCircle size={14} />
            <span className="hidden sm:inline">{t.cta}</span>
            <span className="sm:hidden">{t.ctaShort}</span>
          </a>
          <button
            data-testid="urgency-dismiss"
            onClick={handleDismiss}
            className="text-white/70 hover:text-white transition-colors p-1"
          >
            <X size={16} />
          </button>
        </div>
      </div>
    </motion.div>
  );
};

export default UrgencyBanner;
